import { WorkSchedule } from '../slices/workScheduleSlice';
import Holidays from 'date-holidays';

export interface VacationRequestSummaryItem {
  startDate: string;
  endDate: string;
  dayFraction?: number;
  vacationDays?: number;
  vacationDaysByYear?: Record<string, number>;
  status: 'pending' | 'approved' | 'declined';
}

export interface VacationAccountUser {
  _id: string;
  employedSince?: string;
  createdAt?: string;
}

const FULL_TIME_VACATION_DAYS = 30;
const FULL_TIME_WORKDAYS_PER_WEEK = 5;
const hd = new Holidays('DE', 'BE');

const WEEKDAY_NAMES_DE = [
  'Sonntag',
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
];

const normalizeDayName = (dayName?: string) => (dayName || '').toLowerCase().trim();

const getScheduleHours = (schedule?: WorkSchedule) => {
  if (!schedule) return 0;

  if (schedule.mode === 'flex') {
    return Math.max(0, schedule.hours || 0);
  }

  const [sh, sm] = (schedule.startTime || '').split(':').map(Number);
  const [eh, em] = (schedule.endTime || '').split(':').map(Number);

  if ([sh, sm, eh, em].some((value) => Number.isNaN(value))) {
    return 0;
  }

  return Math.max(0, eh + em / 60 - (sh + sm / 60));
};

const getWorkScheduleForDate = (
  schedules: WorkSchedule[],
  userId: string,
  date: Date
) => {
  const weekday = WEEKDAY_NAMES_DE[date.getDay()];
  const dateTime = date.getTime();
  const matchingDay = schedules.filter(
    (schedule) =>
      schedule.userId === userId &&
      normalizeDayName(schedule.dayOfWeek) === normalizeDayName(weekday)
  );

  const historical = matchingDay
    .filter((schedule) => {
      const validFrom = schedule.validFrom ? new Date(schedule.validFrom).getTime() : null;
      const validTo = schedule.validTo ? new Date(schedule.validTo).getTime() : null;
      return validFrom !== null && validFrom <= dateTime && (validTo === null || validTo >= dateTime);
    })
    .sort(
      (a, b) =>
        new Date(b.validFrom || 0).getTime() - new Date(a.validFrom || 0).getTime()
    );

  return historical[0] || matchingDay.find((schedule) => !schedule.validFrom);
};

const normalizeDate = (value: Date | string) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const roundVacationDays = (days: number) => Math.round(days * 100) / 100;

const normalizeDayFraction = (value: unknown) => (Number(value) === 0.5 ? 0.5 : 1);

const toDateKey = (date: Date | string) => {
  const value = new Date(date);
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isWeekday = (date: Date) => {
  const day = date.getDay();
  return day !== 0 && day !== 6;
};

const isHoliday = (date: Date) => {
  const holidays = hd.getHolidays(date.getFullYear());
  return holidays.some(
    (holiday) =>
      holiday.type === 'public' &&
      holiday.date.split(' ')[0] === toDateKey(date)
  );
};

const isCountableVacationDay = (date: Date) => isWeekday(date) && !isHoliday(date);

const getVacationDayValueForDate = (
  schedules: WorkSchedule[],
  userId: string,
  date: Date
) => {
  if (!isCountableVacationDay(date)) return 0;

  const schedule = getWorkScheduleForDate(schedules, userId, date);
  const scheduledHours = getScheduleHours(schedule);

  if (scheduledHours <= 0) return 0;

  return 1;
};

const countVacationDaysInRange = (
  schedules: WorkSchedule[],
  userId: string,
  startDate: Date | string,
  endDate: Date | string,
) => {
  const start = normalizeDate(startDate);
  const end = normalizeDate(endDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
    return 0;
  }

  let days = 0;

  for (let current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
    days += getVacationDayValueForDate(schedules, userId, current);
  }

  return roundVacationDays(days);
};

const getYearBoundaries = (year: number) => ({
  start: new Date(year, 0, 1),
  end: new Date(year, 11, 31),
});

const prorateEntitlementFromStartDate = (
  entitlement: number,
  year: number,
  accountStartDate?: string
) => {
  if (!accountStartDate) return 0;

  const startDate = normalizeDate(accountStartDate);
  if (Number.isNaN(startDate.getTime())) return 0;

  const accountStartYear = startDate.getFullYear();

  if (year < accountStartYear) return 0;
  if (year > accountStartYear) return entitlement;

  const employedMonthsInYear = 12 - startDate.getMonth();

  return roundVacationDays(entitlement * (employedMonthsInYear / 12));
};

const scheduleOverlapsYear = (schedule: WorkSchedule, yearStart: Date, yearEnd: Date) => {
  const validFrom = schedule.validFrom ? new Date(schedule.validFrom) : null;
  const validTo = schedule.validTo ? new Date(schedule.validTo) : null;

  return (!validFrom || validFrom <= yearEnd) && (!validTo || validTo >= yearStart);
};

export const getWeeklyWorkdays = (schedules: WorkSchedule[], userId: string) => {
  const userSchedules = schedules.filter((schedule) => schedule.userId === userId);
  return userSchedules.reduce((total, schedule) => {
    return total + (getScheduleHours(schedule) > 0 ? 1 : 0);
  }, 0);
};

export const getAnnualVacationEntitlement = (
  schedules: WorkSchedule[],
  userId: string,
  year: number,
  user?: VacationAccountUser
) => {
  if (!user?.employedSince) return 0;

  const { start: yearStart, end: yearEnd } = getYearBoundaries(year);
  const latestScheduleByDay = new Map<string, WorkSchedule>();

  schedules
    .filter(
      (schedule) =>
        schedule.userId === userId &&
        scheduleOverlapsYear(schedule, yearStart, yearEnd)
    )
    .forEach((schedule) => {
      const dayKey = normalizeDayName(schedule.dayOfWeek);
      const current = latestScheduleByDay.get(dayKey);
      const scheduleValidFrom = new Date(schedule.validFrom || 0).getTime();
      const currentValidFrom = current ? new Date(current.validFrom || 0).getTime() : -1;

      if (!current || scheduleValidFrom >= currentValidFrom) {
        latestScheduleByDay.set(dayKey, schedule);
      }
    });

  const weeklyWorkdays = Array.from(latestScheduleByDay.values()).filter(
    (schedule) => getScheduleHours(schedule) > 0
  ).length;

  if (weeklyWorkdays <= 0) return 0;

  const annualEntitlement = roundVacationDays(
    (FULL_TIME_VACATION_DAYS / FULL_TIME_WORKDAYS_PER_WEEK) * weeklyWorkdays
  );

  return prorateEntitlementFromStartDate(
    annualEntitlement,
    year,
    user.employedSince
  );
};

export const getVacationDaysForYearByStatus = (
  schedules: WorkSchedule[],
  userId: string,
  requests: VacationRequestSummaryItem[],
  year: number
) => {
  const { start: yearStart, end: yearEnd } = getYearBoundaries(year);

  const summary = requests.reduce(
    (summary, request) => {
      const rangeStart = normalizeDate(request.startDate);
      const rangeEnd = normalizeDate(request.endDate);

      if (rangeEnd < yearStart || rangeStart > yearEnd) {
        return summary;
      }

      const effectiveStart = rangeStart < yearStart ? yearStart : rangeStart;
      const effectiveEnd = rangeEnd > yearEnd ? yearEnd : rangeEnd;
      const approvedDaysForYear = request.vacationDaysByYear?.[year.toString()];
      const days =
        request.status === 'approved' && typeof approvedDaysForYear === 'number'
          ? approvedDaysForYear
          : request.status === 'approved' && typeof request.vacationDays === 'number'
            ? request.vacationDays
          : countVacationDaysInRange(
              schedules,
              userId,
              effectiveStart,
              effectiveEnd
            ) * normalizeDayFraction(request.dayFraction);

      if (request.status === 'approved') {
        summary.approved += days;
      } else if (request.status === 'pending') {
        summary.pending += days;
      } else if (request.status === 'declined') {
        summary.declined += days;
      }

      return summary;
    },
    { approved: 0, pending: 0, declined: 0 }
  );

  return {
    approved: roundVacationDays(summary.approved),
    pending: roundVacationDays(summary.pending),
    declined: roundVacationDays(summary.declined),
  };
};

export const getVacationAccountSummary = (
  schedules: WorkSchedule[],
  userId: string,
  requests: VacationRequestSummaryItem[],
  year: number,
  user?: VacationAccountUser
) => {
  const accountStartDate = user?.employedSince;
  const accountStartYear = accountStartDate
    ? new Date(accountStartDate).getFullYear()
    : year;

  let carried = 0;

  for (let currentYear = accountStartYear; currentYear < year; currentYear += 1) {
    const entitlement = getAnnualVacationEntitlement(schedules, userId, currentYear, user);
    const { approved, pending } = getVacationDaysForYearByStatus(
      schedules,
      userId,
      requests,
      currentYear
    );

    carried = roundVacationDays(Math.max(0, carried + entitlement - approved - pending));
  }

  const annualEntitlement = getAnnualVacationEntitlement(schedules, userId, year, user);
  const { approved, pending, declined } = getVacationDaysForYearByStatus(
    schedules,
    userId,
    requests,
    year
  );

  return {
    annualEntitlement,
    previousYearRemaining: carried,
    entitlement: roundVacationDays(annualEntitlement + carried),
    carried,
    approved,
    pending,
    declined,
    remaining: roundVacationDays(annualEntitlement + carried - approved - pending),
  };
};
